import { faPlus } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ReactElement } from 'react'
import { ActionsMenu } from '~/components/Global/Interactions/ActionsMenu/ActionsMenu'
import { ActionsMenuItem } from '~/components/Global/Interactions/ActionsMenu/ActionsMenuItem'
import { ActionsMenuItemGroup } from '~/components/Global/Interactions/ActionsMenu/ActionsMenuItemGroup'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import type { FilterDefinition, FiltersState } from './types'
import { definitionKey, isFilterActive } from './utils'

type AddFilterButtonProps = {
  definitions: FilterDefinition[]
  filters: FiltersState
  onSelect: (def: FilterDefinition) => void
}

export const AddFilterButton = ({
  definitions,
  filters,
  onSelect
}: AddFilterButtonProps): ReactElement | null => {
  const inactiveDefs = definitions.filter(
    (def) => !isFilterActive(def, filters)
  )

  if (inactiveDefs.length === 0) return null

  type MenuEntry =
    | { type: 'item'; def: FilterDefinition }
    | { type: 'group'; label: string; items: FilterDefinition[] }

  const entries: MenuEntry[] = []
  const groupMap = new Map<string, FilterDefinition[]>()

  for (const def of inactiveDefs) {
    if (def.group) {
      let items = groupMap.get(def.group)
      if (!items) {
        items = []
        groupMap.set(def.group, items)
        entries.push({ type: 'group', label: def.group, items })
      }
      items.push(def)
    } else {
      entries.push({ type: 'item', def })
    }
  }

  return (
    <ActionsMenu
      triggerOn="click"
      trigger={(props) => (
        <span {...props}>
          <Button
            variation={ButtonVariation.PrimaryInversed}
            buttonSize="small"
            type="button"
            aria-label="Ajouter un filtre"
          >
            <FontAwesomeIcon icon={faPlus} />
            Ajouter un filtre
          </Button>
        </span>
      )}
    >
      {entries.map((entry) =>
        entry.type === 'group' ? (
          <ActionsMenuItemGroup
            key={`group-${entry.label}`}
            label={entry.label}
          >
            {entry.items.map((def) => (
              <ActionsMenuItem
                key={definitionKey(def)}
                title={def.menuLabel ?? def.label}
                onClick={() => onSelect(def)}
              />
            ))}
          </ActionsMenuItemGroup>
        ) : (
          <ActionsMenuItem
            key={definitionKey(entry.def)}
            title={entry.def.menuLabel ?? entry.def.label}
            onClick={() => onSelect(entry.def)}
          />
        )
      )}
    </ActionsMenu>
  )
}
